import React, { useId, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, type View } from 'react-native';
import {
  IconCaretDownFilled,
  IconCaretUpFilled,
  IconCheck,
  IconInfoCircle,
  IconLock,
  IconX,
} from '@tabler/icons-react-native';

import { Box, Text } from 'app/shopify/theme';

import {
  FieldAction,
  FieldBorder,
  FieldFrame,
  FieldIcon,
  useField,
  type FieldFrameProps,
  type FieldIconSlot,
  type FieldSize,
  type FieldState,
  type FieldValidation,
} from '../field';

export interface SelectFieldOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends FieldFrameProps {
  options: readonly SelectFieldOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  size?: FieldSize;
  state?: FieldState;
  validation?: FieldValidation;
  disabled?: boolean;
  readOnly?: boolean;
  leadingIcon?: FieldIconSlot;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  closeAccessibilityLabel?: string;
  emptyText?: string;
  testID?: string;
}

export function SelectField({
  options,
  value,
  onValueChange,
  label = 'Label',
  placeholder = 'Placeholder',
  helperText,
  errorMessage,
  size = 'default',
  state = 'enabled',
  validation = 'none',
  disabled = false,
  readOnly = false,
  leadingIcon,
  showInfoIcon = false,
  onInfoPress,
  infoAccessibilityLabel = 'More information',
  accessibilityLabel,
  accessibilityHint,
  closeAccessibilityLabel = 'Close options',
  emptyText = 'No options available',
  containerStyle,
  testID,
}: SelectFieldProps) {
  const id = useId();
  const triggerRef = useRef<View>(null);
  const selectedRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const field = useField({
    size,
    state: open ? 'active' : state,
    validation,
    disabled: disabled || state === 'disabled',
    readOnly: readOnly || state === 'readOnly',
  });
  const { theme } = field;
  const selected = options.find((option) => option.value === value);
  const description = field.error ? errorMessage : helperText;
  const descriptionId = `${id}-description`;
  const labelId = `${id}-label`;
  const initialOption =
    options.find((option) => !option.disabled && option.value === value) ?? options.find((option) => !option.disabled);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <FieldFrame
      field={field}
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      labelId={labelId}
      descriptionId={descriptionId}
      containerStyle={containerStyle}
    >
      <Pressable
        ref={triggerRef}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? [label, selected?.label ?? placeholder].filter(Boolean).join(', ')}
        accessibilityHint={accessibilityHint ?? description}
        accessibilityState={{ disabled: !field.interactive, expanded: open && field.interactive }}
        accessibilityValue={{ text: selected?.label ?? placeholder }}
        aria-invalid={field.error}
        {...(Platform.OS === 'web'
          ? { 'aria-haspopup': 'dialog' as const, 'aria-describedby': description ? descriptionId : undefined }
          : {})}
        disabled={!field.interactive}
        onPress={() => setOpen(true)}
        onFocus={() => field.setFocused(true)}
        onBlur={() => field.setFocused(false)}
        onHoverIn={() => field.setHovered(true)}
        onHoverOut={() => field.setHovered(false)}
        style={{
          minHeight: field.height,
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing['spacing.general.x1'],
          padding: theme.spacing['spacing.general.x1'],
          borderRadius: theme.borderRadii['radius.component.fields.input'],
          backgroundColor: theme.colors['surface.default'],
        }}
      >
        <SelectFieldValue field={field} selected={selected} placeholder={placeholder} leadingIcon={leadingIcon} />
        <SelectFieldIndicators
          field={field}
          open={open}
          state={state}
          showInfoIcon={showInfoIcon}
          onInfoPress={onInfoPress}
          infoAccessibilityLabel={infoAccessibilityLabel}
        />
        <FieldBorder field={field} />
      </Pressable>
      <SelectFieldOptions
        field={field}
        open={open}
        close={close}
        triggerRef={triggerRef}
        selectedRef={selectedRef}
        accessibilityLabel={accessibilityLabel}
        label={label}
        placeholder={placeholder}
        closeAccessibilityLabel={closeAccessibilityLabel}
        emptyText={emptyText}
        options={options}
        initialOption={initialOption}
        value={value}
        onValueChange={onValueChange}
      />
    </FieldFrame>
  );
}

function SelectFieldOptions({
  field,
  open,
  close,
  triggerRef,
  selectedRef,
  accessibilityLabel,
  label,
  placeholder,
  closeAccessibilityLabel = 'Close options',
  emptyText,
  options,
  initialOption,
  value,
  onValueChange,
}: Pick<
  SelectFieldProps,
  | 'accessibilityLabel'
  | 'label'
  | 'placeholder'
  | 'closeAccessibilityLabel'
  | 'emptyText'
  | 'options'
  | 'value'
  | 'onValueChange'
> & {
  field: ReturnType<typeof useField>;
  open: boolean;
  close: () => void;
  triggerRef: React.RefObject<View | null>;
  selectedRef: React.RefObject<View | null>;
  initialOption: SelectFieldOption | undefined;
}) {
  const { theme } = field;
  return (
    <Modal
      transparent
      visible={open && field.interactive}
      animationType="fade"
      onRequestClose={close}
      onDismiss={() => triggerRef.current?.focus()}
      onShow={() => selectedRef.current?.focus()}
    >
      <Box
        flex={1}
        justifyContent="center"
        alignItems="center"
        padding="spacing.general.x6"
        backgroundColor="surface.overlay"
      >
        <Box
          accessibilityViewIsModal
          role="dialog"
          accessibilityLabel={accessibilityLabel ?? label ?? placeholder}
          backgroundColor="surface.default"
          borderRadius="radius.component.fields.input"
          padding="spacing.general.x4"
          gap="spacing.general.x3"
          width="100%"
          maxWidth={480}
          maxHeight="80%"
        >
          <Box flexDirection="row" alignItems="center" gap="spacing.general.x2">
            <Text variant="bodyBaseMedium" flex={1}>
              {label || accessibilityLabel || placeholder}
            </Text>
            <FieldAction
              icon={<IconX strokeWidth={2} />}
              accessibilityLabel={closeAccessibilityLabel}
              onPress={close}
              size={field.buttonSize}
              iconSize={field.iconSize}
            />
          </Box>
          <ScrollView keyboardShouldPersistTaps="handled">
            {options.length === 0 ? (
              <Text variant="bodyBase" color="text.secondary">
                {emptyText}
              </Text>
            ) : (
              options.map((option) => (
                <Pressable
                  key={option.value}
                  ref={option === initialOption ? selectedRef : undefined}
                  accessibilityRole="radio"
                  accessibilityLabel={option.label}
                  accessibilityState={{ checked: option.value === value, disabled: option.disabled ?? false }}
                  disabled={option.disabled}
                  onPress={() => {
                    if (!field.interactive || option.disabled) return;
                    onValueChange?.(option.value);
                    close();
                  }}
                  style={({ pressed }) => ({
                    padding: theme.spacing['spacing.general.x3'],
                    minHeight: field.height,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing['spacing.general.x2'],
                    backgroundColor: pressed ? theme.colors['state-layer.press'] : 'transparent',
                  })}
                >
                  <Text
                    variant={option.value === value ? 'bodyBaseMedium' : 'bodyBase'}
                    color={option.disabled ? 'text.disabled' : 'text.primary'}
                    flex={1}
                  >
                    {option.label}
                  </Text>
                  {option.value === value ? (
                    <IconCheck size={field.iconSize} color={theme.colors['icon.primary']} />
                  ) : null}
                </Pressable>
              ))
            )}
          </ScrollView>
        </Box>
      </Box>
    </Modal>
  );
}

function SelectFieldIndicators({
  field,
  open,
  state,
  showInfoIcon,
  onInfoPress,
  infoAccessibilityLabel = 'More information',
}: Pick<SelectFieldProps, 'state' | 'showInfoIcon' | 'onInfoPress' | 'infoAccessibilityLabel'> & {
  field: ReturnType<typeof useField>;
  open: boolean;
}) {
  const { theme } = field;
  const Caret = open || state === 'active' ? IconCaretUpFilled : IconCaretDownFilled;
  return (
    <>
      {showInfoIcon ? (
        onInfoPress ? (
          <FieldAction
            icon={<IconInfoCircle strokeWidth={2} />}
            size={field.buttonSize}
            iconSize={field.iconSize}
            accessibilityLabel={infoAccessibilityLabel}
            onPress={onInfoPress}
            disabled={field.isDisabled}
          />
        ) : (
          <Box width={field.buttonSize} alignItems="center">
            <IconInfoCircle size={field.iconSize} color={field.iconColor} strokeWidth={2} />
          </Box>
        )
      ) : null}
      {field.isReadOnly ? (
        <Box width={field.buttonSize} alignItems="center">
          <IconLock size={field.iconSize} color={field.iconColor} strokeWidth={2} />
        </Box>
      ) : null}
      <Box width={field.buttonSize} minHeight={field.buttonSize} alignItems="center" justifyContent="center">
        <Caret size={field.iconSize} color={theme.colors[field.isDisabled ? 'icon.disabled' : 'icon.primary']} />
      </Box>
    </>
  );
}

function SelectFieldValue({
  field,
  selected,
  placeholder,
  leadingIcon,
}: Pick<SelectFieldProps, 'placeholder' | 'leadingIcon'> & {
  field: ReturnType<typeof useField>;
  selected?: SelectFieldOption;
}) {
  const { theme } = field;
  return (
    <Box
      flex={1}
      minWidth={0}
      flexDirection="row"
      alignItems="center"
      gap="spacing.general.x1"
      paddingLeft={field.small ? 'spacing.general.x2' : 'spacing.general.x3'}
    >
      {leadingIcon ? <FieldIcon icon={leadingIcon} size={field.iconSize} color={field.iconColor} /> : null}
      <Text
        variant={selected ? 'bodyBaseMedium' : 'bodyBase'}
        numberOfLines={1}
        flex={1}
        paddingHorizontal="spacing.general.x1"
        style={{ color: selected ? field.textColor : theme.colors['text.tertiary'] }}
      >
        {selected?.label ?? placeholder}
      </Text>
    </Box>
  );
}
